/**
 * Custom hook for fetching buyer users
 * Handles GraphQL query for buyer users with proper error handling and state management
 */

import { useCallback } from 'react';

import { useGetBuyerUsersQueryQuery } from '../../../generated';

export interface BuyerUser {
  id: string;
  name?: string | null;
  phone?: string;
  profileImage?: {
    __typename?: string;
    url?: string | null;
  } | null;
}

export interface UseBuyerUsersConfig {
  buyerId: string;
  isEnabled?: boolean;
  pageSize?: number;
}

export interface UseBuyerUsersResponse {
  users: BuyerUser[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useBuyerUsers = ({
  buyerId,
  isEnabled = true,
  pageSize = 50,
}: UseBuyerUsersConfig): UseBuyerUsersResponse => {
  const { data, loading, error, refetch } = useGetBuyerUsersQueryQuery({
    variables: { buyerId, first: pageSize },
    skip: !isEnabled || !buyerId,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const users: BuyerUser[] =
    data?.buyer?.users?.edges?.map((edge) => edge.node) || [];

  const handleRefetch = useCallback(async () => {
    await refetch();
  }, [refetch]);

  return {
    users,
    isLoading: loading,
    error: error || null,
    refetch: handleRefetch,
  };
};
